import { Badge, Stack, Text, Title } from "@mantine/core";
import type { DataTableColumn } from "mantine-datatable";
import { DataTable } from "mantine-datatable";
import React, { useMemo, useState } from "react";
import { client } from "@/api-client";
import type {
  AdminOwnerBasicOutput,
  AdminOwnerDetailOutput,
} from "@/api-types";
import { AccountDetails } from "../components/AccountDetails";

const Owners = () => {
  const { data: ownersData, isLoading } = client.useQuery(
    "get",
    "/admin/owners",
  );
  const [selectedOwner, setSelectedOwner] =
    useState<AdminOwnerBasicOutput | null>(null);
  const [selectedOwnerDetails, setSelectedOwnerDetails] =
    useState<AdminOwnerDetailOutput | null>(null);

  // Query for detailed owner information when an owner is selected
  const { data: ownerDetailsData, isLoading: isLoadingDetails } =
    client.useQuery(
      "get",
      "/admin/owners/{owner_id}",
      {
        params: { path: { owner_id: selectedOwner?.id || "" } },
      },
      {
        enabled: !!selectedOwner?.id,
      },
    );

  // Update detailed owner data when query completes
  React.useEffect(() => {
    if (ownerDetailsData) {
      setSelectedOwnerDetails(ownerDetailsData);
    }
  }, [ownerDetailsData]);

  const columns = useMemo<DataTableColumn<AdminOwnerBasicOutput>[]>(
    () => [
      {
        accessor: "name",
        title: "Name",
        render: (owner) => `${owner.first_name} ${owner.last_name}`,
      },
      {
        accessor: "email",
        title: "Email",
      },
      {
        accessor: "locality",
        title: "Location",
      },
      {
        accessor: "created_at",
        title: "Created",
        render: (owner) => new Date(owner.created_at).toLocaleDateString(),
      },
      {
        accessor: "status",
        title: "Status",
        render: (owner) => (
          <Badge
            color={owner.status === "NORMAL" ? "green" : "red"}
            variant="light"
          >
            {owner.status}
          </Badge>
        ),
      },
    ],
    [],
  );

  if (isLoading) {
    return <Text>Loading owners...</Text>;
  }

  if (!ownersData) {
    return <Text>No owners data available.</Text>;
  }

  return (
    <Stack gap="xl" h="100%">
      <Title order={1}>Owners ({ownersData.owners.length})</Title>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 200px)",
        }}
      >
        {/* Table Section */}
        <div
          style={{
            flex: selectedOwner ? "0 0 50%" : "1",
            overflow: "auto",
            paddingBottom: "1rem",
          }}
        >
          <DataTable
            columns={columns}
            records={ownersData.owners}
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            minHeight={180}
            noRecordsText="No owners found"
            onRowClick={({ record }) => {
              setSelectedOwner(record);
              setSelectedOwnerDetails(null); // Clear previous details while loading new ones
            }}
            selectedRecords={selectedOwner ? [selectedOwner] : []}
          />
        </div>

        {/* Details Panel */}
        {selectedOwner && (
          <div
            style={{
              flex: "0 0 50%",
              overflow: "auto",
              paddingTop: "1rem",
              borderTop: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            {isLoadingDetails ? (
              <Text>Loading owner details...</Text>
            ) : selectedOwnerDetails ? (
              <AccountDetails
                account={selectedOwnerDetails}
                accountType="owner"
              />
            ) : (
              <Text>Failed to load owner details.</Text>
            )}
          </div>
        )}
      </div>
    </Stack>
  );
};

export default Owners;
